import type { EstadoManejoVidro } from './manejoVidros'

export type ConfiguracaoOficinaCorte = 'quatroRodas' | 'tombado' | 'capotado'

export type FerramentaOficinaCorte =
  | 'cortadorHidraulico'
  | 'alargador'
  | 'extensor'
  | 'hooligan'
  | 'puncaoVidro'
  | 'machadinha'
  | 'serraSabre'
  | 'vstrut'
  | 'calcoEscalonado'
  | 'blocoCribbing'

export const CONFIGURACOES: readonly {
  id: ConfiguracaoOficinaCorte
  nome: string
  descricao: string
}[] = [
  { id: 'quatroRodas', nome: 'Sobre quatro rodas', descricao: 'Veículo estável sobre as rodas.' },
  { id: 'tombado', nome: 'Tombado', descricao: 'Veículo apoiado na lateral; acesso e apoio devem ser definidos antes do corte.' },
  { id: 'capotado', nome: 'Capotado', descricao: 'Veículo invertido; considerar apoio no cofre e rota posterior.' },
]

export const EQUIPAMENTOS: readonly {
  id: FerramentaOficinaCorte
  nome: string
  funcao: string
}[] = [
  { id: 'cortadorHidraulico', nome: 'Cortador hidráulico', funcao: 'Ferramenta de corte estrutural.' },
  { id: 'alargador', nome: 'Alargador', funcao: 'Criação e ampliação controlada de espaço.' },
  { id: 'extensor', nome: 'Extensor', funcao: 'Empuxo e deslocamento controlado de componentes.' },
  { id: 'hooligan', nome: 'Hooligan', funcao: 'Criação manual de uma abertura inicial.' },
  { id: 'vstrut', nome: 'V-Strut', funcao: 'Estabilização regulável em posições instáveis.' },
  { id: 'calcoEscalonado', nome: 'Calço escalonado', funcao: 'Apoio sob a estrutura e anulação de folgas.' },
  { id: 'blocoCribbing', nome: 'Bloco', funcao: 'Preenchimento e apoio modular de vazios.' },
]

export interface ResultadoIntervencaoPorta {
  permitido: boolean
  rompeuVidro: boolean
  novoEstadoVidro: EstadoManejoVidro
  mensagem: string
}

/**
 * Mantém a avaliação usada pela primeira interface da oficina. O motor de
 * operações abaixo aplica a mesma regra a cada passo da técnica.
 */
export function avaliarIntervencaoNaPorta(
  ferramenta: FerramentaOficinaCorte,
  estadoVidro: EstadoManejoVidro,
): ResultadoIntervencaoPorta {
  const ferramentaDePorta = ['cortadorHidraulico', 'alargador', 'extensor', 'hooligan'].includes(ferramenta)
  if (!ferramentaDePorta) {
    return {
      permitido: false,
      rompeuVidro: false,
      novoEstadoVidro: estadoVidro,
      mensagem: 'Esta ferramenta prepara a estabilização. Selecione uma ferramenta de intervenção para atuar na porta.',
    }
  }
  if (estadoVidro === 'intacto') {
    return {
      permitido: false,
      rompeuVidro: true,
      novoEstadoVidro: 'quebrado',
      mensagem: 'Ruptura não controlada: a deformação da porta rompeu o vidro ainda intacto. Interrompa, proteja a vítima/equipe e faça o manejo de vidro antes de retomar.',
    }
  }
  return {
    permitido: true,
    rompeuVidro: false,
    novoEstadoVidro: estadoVidro,
    mensagem: 'Vidro previamente gerenciado. A porta pode receber a intervenção selecionada.',
  }
}

export type IdVinculoPorta =
  | 'dobradicaSuperior'
  | 'dobradicaInferior'
  | 'limitador'
  | 'trava'
  | 'fiacao'

export type EstadoVinculoPorta = 'integro' | 'deformado' | 'liberado'
export type EstadoChapaPorta = 'integra' | 'deformada' | 'desfolhada'
export type TecnicaCriacaoEspaco =
  | 'hooligan'
  | 'amassamentoPorta'
  | 'pincamento'
  | 'compressaoMacaneta'
  | 'amassamentoParalama'
  | 'aberturaJanela'

export interface EstadoPortaOficina {
  abertura: number
  chapa: EstadoChapaPorta
  vinculos: Record<IdVinculoPorta, EstadoVinculoPorta>
  vidro: EstadoManejoVidro
  sustentada: boolean
  removida: boolean
  testeManualRealizado: boolean
  tecnicaCriacaoEspaco: TecnicaCriacaoEspaco | null
  esquadria: 'integra' | 'deformada'
  paralama: 'integro' | 'deformado'
}

export type OperacaoPorta =
  | 'testarAberturaManual'
  | 'criarAbertura'
  | 'amassarPorta'
  | 'pincarPorta'
  | 'comprimirMacaneta'
  | 'amassarParalama'
  | 'abrirPelaJanela'
  | 'desfolharPorta'
  | 'expandirTrava'
  | 'expandirDobradiçaSuperior'
  | 'expandirDobradiçaInferior'
  | 'cortarDobradiçaSuperior'
  | 'cortarDobradiçaInferior'
  | 'cortarLimitador'
  | 'cortarFiacao'
  | 'sustentarPorta'
  | 'removerPorta'

export type IdZonaOperacaoPorta =
  | 'chapa'
  | 'macaneta'
  | 'trava'
  | 'dobradicaSuperior'
  | 'dobradicaInferior'
  | 'limitador'
  | 'fiacao'
  | 'esquadria'
  | 'paralama'

export const ZONAS_POR_OPERACAO: Readonly<Record<OperacaoPorta, readonly IdZonaOperacaoPorta[]>> = {
  testarAberturaManual: [],
  criarAbertura: ['chapa', 'trava'],
  amassarPorta: ['chapa'],
  pincarPorta: ['chapa'],
  comprimirMacaneta: ['macaneta'],
  amassarParalama: ['paralama'],
  abrirPelaJanela: ['esquadria'],
  desfolharPorta: ['chapa', 'trava'],
  expandirTrava: ['trava'],
  expandirDobradiçaSuperior: ['dobradicaSuperior'],
  expandirDobradiçaInferior: ['dobradicaInferior'],
  cortarDobradiçaSuperior: ['dobradicaSuperior'],
  cortarDobradiçaInferior: ['dobradicaInferior'],
  cortarLimitador: ['limitador'],
  cortarFiacao: ['fiacao'],
  sustentarPorta: [],
  removerPorta: [],
}

export const OPERACOES_PORTA: readonly {
  id: OperacaoPorta
  nome: string
  instrucao: string
  usaFerramenta: boolean
}[] = [
  { id: 'testarAberturaManual', nome: 'Testar abertura manual', instrucao: 'Verifique primeiro se a porta abre sem ferramenta.', usaFerramenta: false },
  { id: 'criarAbertura', nome: 'Criar abertura com Hooligan', instrucao: 'Insira e use a alavanca para criar o espaço inicial.', usaFerramenta: true },
  { id: 'amassarPorta', nome: 'Amassar a porta', instrucao: 'Comprima a chapa com o alargador para formar uma prega de acesso.', usaFerramenta: true },
  { id: 'pincarPorta', nome: 'Pinçar a porta', instrucao: 'Use as ponteiras do alargador para pinçar e afastar a chapa.', usaFerramenta: true },
  { id: 'amassarParalama', nome: 'Amassar o para-lama', instrucao: 'Pince e esmague o topo do para-lama junto à Coluna A para criar acesso à dobradiça.', usaFerramenta: true },
  { id: 'comprimirMacaneta', nome: 'Comprimir a maçaneta', instrucao: 'Feche o alargador sobre a maçaneta para criar acesso à trava.', usaFerramenta: true },
  { id: 'abrirPelaJanela', nome: 'Criar espaço pela janela', instrucao: 'Apoie uma ponta na esquadria superior e outra na inferior; abra para criar acesso.', usaFerramenta: true },
  { id: 'desfolharPorta', nome: 'Desfolhar a porta', instrucao: 'Após criar espaço, separe progressivamente a chapa externa com o alargador.', usaFerramenta: true },
  { id: 'expandirTrava', nome: 'Expandir no pino da trava', instrucao: 'Aplique o alargador na região da trava.', usaFerramenta: true },
  { id: 'expandirDobradiçaSuperior', nome: 'Expandir dobradiça superior', instrucao: 'Libere a dobradiça por expansão.', usaFerramenta: true },
  { id: 'expandirDobradiçaInferior', nome: 'Expandir dobradiça inferior', instrucao: 'Libere a dobradiça por expansão.', usaFerramenta: true },
  { id: 'cortarDobradiçaSuperior', nome: 'Cortar dobradiça superior', instrucao: 'Alternativa quando a expansão não for adotada.', usaFerramenta: true },
  { id: 'cortarDobradiçaInferior', nome: 'Cortar dobradiça inferior', instrucao: 'Alternativa quando a expansão não for adotada.', usaFerramenta: true },
  { id: 'cortarLimitador', nome: 'Liberar limitador', instrucao: 'Corte ou libere o limitador da porta.', usaFerramenta: true },
  { id: 'cortarFiacao', nome: 'Liberar fiação', instrucao: 'Libere a fiação que ainda conecta a porta.', usaFerramenta: true },
  { id: 'sustentarPorta', nome: 'Sustentar a porta', instrucao: 'Prepare o recebimento da peça antes da retirada.', usaFerramenta: false },
  { id: 'removerPorta', nome: 'Retirar a porta', instrucao: 'Conclua somente após liberar todos os vínculos.', usaFerramenta: false },
]

export interface ComandoOperacaoPorta {
  operacao: OperacaoPorta
  ferramenta?: FerramentaOficinaCorte
  zona?: IdZonaOperacaoPorta | null
  orientacaoValida?: boolean
  portaDianteira?: boolean
}

export type CodigoResultadoOperacaoPorta =
  | 'concluida'
  | 'semProgresso'
  | 'ferramentaIncompativel'
  | 'aberturaInsuficiente'
  | 'vidroRompeu'
  | 'vinculosRestantes'
  | 'portaSemApoio'
  | 'portaJaRemovida'
  | 'posicaoAusente'
  | 'zonaIncompativel'
  | 'orientacaoIncompativel'
  | 'portaIncompativel'

export interface ResultadoOperacaoPorta {
  codigo: CodigoResultadoOperacaoPorta
  concluiu: boolean
  estado: EstadoPortaOficina
  mensagem: string
  alteracoes: readonly string[]
}

const VINCULOS: readonly IdVinculoPorta[] = [
  'dobradicaSuperior',
  'dobradicaInferior',
  'limitador',
  'trava',
  'fiacao',
]

export function estadoInicialPortaOficina(
  vidro: EstadoManejoVidro = 'intacto',
): EstadoPortaOficina {
  return {
    abertura: 0,
    chapa: 'integra',
    vinculos: {
      dobradicaSuperior: 'integro',
      dobradicaInferior: 'integro',
      limitador: 'integro',
      trava: 'integro',
      fiacao: 'integro',
    },
    vidro,
    sustentada: false,
    removida: false,
    testeManualRealizado: false,
    tecnicaCriacaoEspaco: null,
    esquadria: 'integra',
    paralama: 'integro',
  }
}

export function vinculosRestantes(estado: EstadoPortaOficina): IdVinculoPorta[] {
  return VINCULOS.filter((id) => estado.vinculos[id] !== 'liberado')
}

export function portaProntaParaRemocao(estado: EstadoPortaOficina): boolean {
  return vinculosRestantes(estado).length === 0 && estado.sustentada && !estado.removida
}

function copiarEstado(estado: EstadoPortaOficina): EstadoPortaOficina {
  return { ...estado, vinculos: { ...estado.vinculos } }
}

function resultado(
  estado: EstadoPortaOficina,
  codigo: CodigoResultadoOperacaoPorta,
  mensagem: string,
  alteracoes: readonly string[] = [],
): ResultadoOperacaoPorta {
  return { codigo, concluiu: codigo === 'concluida', estado, mensagem, alteracoes }
}

function ferramentaAceita(
  ferramenta: FerramentaOficinaCorte | undefined,
  aceitas: readonly FerramentaOficinaCorte[],
): boolean {
  return ferramenta !== undefined && aceitas.includes(ferramenta)
}

/**
 * Executa uma única ação observável sobre a porta. O domínio preserva
 * resultados parciais e explica por que uma tentativa não avançou, permitindo
 * que a interface anime a ferramenta sem fingir que a estrutura cedeu.
 */
export function executarOperacaoPorta(
  atual: EstadoPortaOficina,
  comando: ComandoOperacaoPorta,
): ResultadoOperacaoPorta {
  const estado = copiarEstado(atual)
  if (estado.removida) {
    return resultado(estado, 'portaJaRemovida', 'A porta já foi removida neste exercício.')
  }

  if (comando.operacao === 'testarAberturaManual') {
    estado.testeManualRealizado = true
    if (estado.vinculos.trava === 'liberado') {
      estado.abertura = Math.max(estado.abertura, 0.35)
      return resultado(estado, 'concluida', 'A porta abriu manualmente.', ['testeManualRealizado', 'abertura'])
    }
    return resultado(estado, 'semProgresso', 'Teste manual realizado: a porta permanece presa pela trava.', ['testeManualRealizado'])
  }

  if (comando.operacao === 'sustentarPorta') {
    estado.sustentada = true
    // Sem ferramenta: a equipe força a porta além do curso, o limitador rompe
    // e o chicote é desconectado à mão.
    const vinculosLeves = (['limitador', 'fiacao'] as const).filter((id) => estado.vinculos[id] !== 'liberado')
    if (vinculosLeves.length === 0) {
      return resultado(estado, 'concluida', 'A porta está sustentada para a liberação final.', ['sustentada'])
    }
    vinculosLeves.forEach((id) => { estado.vinculos[id] = 'liberado' })
    return resultado(
      estado,
      'concluida',
      'A porta está sustentada; forçada além do curso, rompeu o limitador, e o chicote foi desconectado à mão.',
      ['sustentada', ...vinculosLeves.map((id) => `vinculos.${id}`)],
    )
  }

  if (comando.operacao === 'removerPorta') {
    const restantes = vinculosRestantes(estado)
    if (restantes.length > 0) {
      return resultado(estado, 'vinculosRestantes', `A porta ainda possui vínculos: ${restantes.join(', ')}.`)
    }
    if (!estado.sustentada) {
      return resultado(estado, 'portaSemApoio', 'Sustente a porta antes de concluir a retirada.')
    }
    estado.removida = true
    estado.abertura = 1
    return resultado(estado, 'concluida', 'Porta removida e abertura liberada.', ['removida', 'abertura'])
  }

  const zonasAceitas = ZONAS_POR_OPERACAO[comando.operacao]
  if (zonasAceitas.length > 0 && !comando.zona) {
    return resultado(estado, 'posicaoAusente', 'Selecione um ponto de aplicação na porta antes de acionar a ferramenta.')
  }
  if (comando.zona && !zonasAceitas.includes(comando.zona)) {
    return resultado(estado, 'zonaIncompativel', 'O ponto selecionado não corresponde à ação escolhida.')
  }
  if (zonasAceitas.length > 0 && comando.orientacaoValida === false) {
    return resultado(estado, 'orientacaoIncompativel', 'A ferramenta está em contato, mas precisa ser orientada corretamente antes do acionamento.')
  }
  if (comando.operacao === 'amassarParalama' && comando.portaDianteira === false) {
    return resultado(estado, 'portaIncompativel', 'O acesso pelo para-lama está disponível somente junto à porta dianteira.')
  }

  const intervencao = avaliarIntervencaoNaPorta(comando.ferramenta ?? 'vstrut', estado.vidro)
  if (intervencao.rompeuVidro) {
    estado.vidro = intervencao.novoEstadoVidro
    return resultado(estado, 'vidroRompeu', intervencao.mensagem, ['vidro'])
  }
  if (!intervencao.permitido) {
    return resultado(estado, 'ferramentaIncompativel', intervencao.mensagem)
  }

  if (comando.operacao === 'criarAbertura') {
    if (!ferramentaAceita(comando.ferramenta, ['hooligan'])) {
      return resultado(estado, 'ferramentaIncompativel', 'Use o Hooligan para esta alternativa de abertura inicial.')
    }
    estado.abertura = Math.max(estado.abertura, 0.12)
    estado.chapa = 'deformada'
    estado.tecnicaCriacaoEspaco = 'hooligan'
    return resultado(estado, 'concluida', 'Abertura inicial criada com o Hooligan.', ['abertura', 'chapa', 'tecnicaCriacaoEspaco'])
  }

  if (comando.operacao === 'comprimirMacaneta') {
    if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
      return resultado(estado, 'ferramentaIncompativel', 'Use o alargador para comprimir a maçaneta.')
    }
    estado.abertura = Math.max(estado.abertura, 0.14)
    estado.chapa = 'deformada'
    estado.tecnicaCriacaoEspaco = 'compressaoMacaneta'
    return resultado(estado, 'concluida', 'Maçaneta comprimida: acesso criado junto à fechadura. O pino da trava ainda precisa ser liberado.', ['abertura', 'chapa', 'tecnicaCriacaoEspaco'])
  }

  if (comando.operacao === 'amassarPorta' || comando.operacao === 'pincarPorta') {
    if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
      return resultado(estado, 'ferramentaIncompativel', 'Esta alternativa exige o alargador.')
    }
    estado.abertura = Math.max(estado.abertura, comando.operacao === 'pincarPorta' ? 0.16 : 0.14)
    estado.chapa = 'deformada'
    estado.tecnicaCriacaoEspaco = comando.operacao === 'pincarPorta' ? 'pincamento' : 'amassamentoPorta'
    return resultado(
      estado,
      'concluida',
      comando.operacao === 'pincarPorta' ? 'A chapa foi pinçada e afastada.' : 'A porta foi amassada para formar uma abertura.',
      ['abertura', 'chapa', 'tecnicaCriacaoEspaco'],
    )
  }

  if (comando.operacao === 'amassarParalama') {
    if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
      return resultado(estado, 'ferramentaIncompativel', 'Use o alargador para deformar o para-lama e criar acesso.')
    }
    estado.abertura = Math.max(estado.abertura, 0.14)
    estado.paralama = 'deformado'
    estado.tecnicaCriacaoEspaco = 'amassamentoParalama'
    return resultado(estado, 'concluida', 'O para-lama foi deformado e abriu acesso à região da dobradiça.', ['abertura', 'paralama', 'tecnicaCriacaoEspaco'])
  }

  if (comando.operacao === 'abrirPelaJanela') {
    if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
      return resultado(estado, 'ferramentaIncompativel', 'Use o alargador apoiado na esquadria da janela.')
    }
    estado.abertura = Math.max(estado.abertura, 0.18)
    estado.esquadria = 'deformada'
    estado.tecnicaCriacaoEspaco = 'aberturaJanela'
    return resultado(estado, 'concluida', 'A esquadria foi deformada e criou espaço pela janela.', ['abertura', 'esquadria', 'tecnicaCriacaoEspaco'])
  }

  if (estado.abertura < 0.1 && comando.operacao !== 'cortarFiacao') {
    return resultado(estado, 'aberturaInsuficiente', 'Ainda não há espaço para posicionar a ferramenta neste ponto.')
  }

  const liberar = (id: IdVinculoPorta, mensagem: string): ResultadoOperacaoPorta => {
    estado.vinculos[id] = 'liberado'
    return resultado(estado, 'concluida', mensagem, [`vinculos.${id}`])
  }

  switch (comando.operacao) {
    case 'desfolharPorta':
      if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
        return resultado(estado, 'ferramentaIncompativel', 'O desfolhamento exige o alargador.')
      }
      estado.chapa = 'desfolhada'
      estado.abertura = Math.max(estado.abertura, 0.35)
      return resultado(estado, 'concluida', 'A chapa externa foi desfolhada, expondo o interior da porta.', ['chapa', 'abertura'])
    case 'expandirTrava':
      if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
        return resultado(estado, 'ferramentaIncompativel', 'A expansão no pino da trava exige o alargador.')
      }
      estado.abertura = Math.max(estado.abertura, 0.45)
      return liberar('trava', 'A trava foi liberada por expansão.')
    case 'expandirDobradiçaSuperior':
    case 'expandirDobradiçaInferior': {
      if (!ferramentaAceita(comando.ferramenta, ['alargador'])) {
        return resultado(estado, 'ferramentaIncompativel', 'A expansão da dobradiça exige o alargador.')
      }
      const id = comando.operacao === 'expandirDobradiçaSuperior' ? 'dobradicaSuperior' : 'dobradicaInferior'
      return liberar(id, 'A dobradiça foi liberada por expansão.')
    }
    case 'cortarDobradiçaSuperior':
    case 'cortarDobradiçaInferior': {
      if (!ferramentaAceita(comando.ferramenta, ['cortadorHidraulico'])) {
        return resultado(estado, 'ferramentaIncompativel', 'O corte da dobradiça exige o cortador hidráulico.')
      }
      const id = comando.operacao === 'cortarDobradiçaSuperior' ? 'dobradicaSuperior' : 'dobradicaInferior'
      return liberar(id, 'A dobradiça foi cortada como alternativa de liberação.')
    }
    case 'cortarLimitador':
      if (!ferramentaAceita(comando.ferramenta, ['cortadorHidraulico'])) {
        return resultado(estado, 'ferramentaIncompativel', 'Use o cortador hidráulico no limitador.')
      }
      estado.vinculos.limitador = 'liberado'
      estado.vinculos.fiacao = 'liberado'
      return resultado(estado, 'concluida', 'O limitador e o chicote elétrico foram liberados.', ['vinculos.limitador', 'vinculos.fiacao'])
    case 'cortarFiacao':
      if (!ferramentaAceita(comando.ferramenta, ['cortadorHidraulico'])) {
        return resultado(estado, 'ferramentaIncompativel', 'Use o cortador hidráulico para liberar a fiação.')
      }
      return liberar('fiacao', 'A fiação foi liberada.')
    default:
      return resultado(estado, 'semProgresso', 'A operação não alterou o estado da porta.')
  }
}
